import { useState, useEffect, useCallback } from 'react'
import {
  Typography,
  Paper,
  Box,
  Alert,
} from '@mui/material'
import ExportButton from '../../components/ExportButton'
import ProgressBar from '../../components/ProgressBar'
import FieldSelector, { FieldConfiguration, SelectedField } from '../../components/FieldSelector'
import { lightspeedApi } from '../../services/api'
import { useFranchise } from '../../contexts/FranchiseContext'
import { usePageTitle } from '../../hooks/usePageTitle'
import { useAsyncExport } from '../../hooks/useAsyncExport'

function LightspeedCustomers() {
  usePageTitle('Lightspeed', 'Customers Export')
  const { fpCode } = useFranchise()

  // Use async export hook
  const {
    loading,
    error: exportError,
    success: exportSuccess,
    progressMessage,
    currentCount,
    startExport
  } = useAsyncExport({
    exportType: 'customers'
  })

  const [fieldConfiguration, setFieldConfiguration] = useState<FieldConfiguration | null>(null)
  const [selectedFields, setSelectedFields] = useState<SelectedField[]>([])
  const [localError, setLocalError] = useState<string | null>(null)

  // Combine errors from hook and local validation
  const error = exportError || localError

  // Load field configuration on mount
  useEffect(() => {
    const loadFieldConfiguration = async () => {
      try {
        const response = await lightspeedApi.getCustomerFields()
        setFieldConfiguration(response.data)
      } catch (error) {
        console.error('Failed to load field configuration:', error)
        setLocalError('Failed to load export field configuration')
      }
    }

    loadFieldConfiguration()
  }, [])

  const handleFieldsChange = useCallback((fields: SelectedField[]) => {
    setSelectedFields(fields)
  }, [])

  const handleExport = async () => {
    if (!fpCode) {
      setLocalError('Please select a franchise partner')
      return
    }

    setLocalError(null)

    // Build export parameters
    const fieldIds = selectedFields.map(f => f.id).join(',')

    const params: Record<string, any> = {
      fp_code: fpCode,
      format: 'xlsx'
    }

    // Only add selected_fields if user has made a selection
    if (fieldIds) {
      params['selected_fields'] = fieldIds
    }

    // Start the async export
    await startExport(params)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {!fpCode && (
          <Alert severity="info">
            Please select a franchise partner from the dropdown above to continue.
          </Alert>
        )}

          {fpCode && (
            <>
              <Paper sx={{ p: 3 }}>
                <FieldSelector
                  configuration={fieldConfiguration}
                  onFieldsChange={handleFieldsChange}
                  exportType="lightspeed_customers"
                />
              </Paper>

          {error && (
            <Alert severity="error" onClose={() => setLocalError(null)}>
              {error}
            </Alert>
          )}

          {exportSuccess && (
            <Alert severity="success">
              {exportSuccess}
            </Alert>
          )}

          {loading && (
            <Box>
              <ProgressBar
                indeterminate
                message={progressMessage || "Exporting customers... This may take a few minutes for large customer lists."}
              />
              {currentCount > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  Records processed: {currentCount.toLocaleString()}
                </Typography>
              )}
            </Box>
          )}

              <ExportButton
                onClick={handleExport}
                loading={loading}
                disabled={!fpCode || selectedFields.length === 0}
                text="Export Customers"
              />

              {selectedFields.length === 0 && (
                <Alert severity="warning">
                  Please select at least one field to export.
                </Alert>
              )}

            </>
          )}
      </Box>
    </Box>
  )
}

export default LightspeedCustomers
